/**
 * Conversations hook — TanStack Query + Supabase Realtime.
 *
 * Subscribes to new messages across every conversation the signed-in user
 * is part of. On arrival we apply a **targeted** cache mutation
 * (`setQueryData`) instead of `invalidateQueries` — bumping just the
 * affected conversation's `unreadCount` + `lastMessageAt` +
 * `lastMessagePreview` and reordering the list — so a busy chat stream
 * doesn't trigger a full refetch per message.
 *
 * Cache miss (first-ever message in a new conversation that hasn't loaded
 * yet) falls back to a single invalidate, which kicks the query into
 * loading the new row.
 */
import { useEffect } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { supabase } from '../supabase'
import i18n from '../i18n/config'
import { messagingService } from '../services/messaging.service'
import { useAuth } from './use-auth'
import type { ConversationListItem } from '../types/messaging.types'

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const conversationKeys = {
  all: ['conversations'] as const,
  list: (userId: string) => [...conversationKeys.all, 'list', userId] as const,
  unreadTotal: (userId: string) => [...conversationKeys.all, 'unread', userId] as const,
}

// ─── Conversations List ─────────────────────────────────────────────────────

export function useConversations() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const userId = user?.id ?? ''

  const query = useQuery({
    queryKey: conversationKeys.list(userId),
    queryFn: () => messagingService.getConversations(userId),
    enabled: !!userId,
    staleTime: 30 * 1000, // 30s — Realtime handles live updates
    placeholderData: keepPreviousData, // Keep showing old data during refetch
  })

  // Realtime: bump the affected conversation in the cached list on every
  // new message. Targeted update — no full refetch.
  useEffect(() => {
    if (!userId) return

    // The list is patched incrementally from realtime; there is no replay for
    // the INSERTs a dropped socket never delivered. On a RE-established
    // subscription, refetch the list so a reconnect cannot leave a stale inbox.
    let hasSubscribedOnce = false

    const channel = supabase
      .channel(`user-conversations:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          const row = payload.new as Record<string, unknown> | null
          if (!row) return

          const conversationId = row.conversation_id as string | undefined
          const senderId = row.sender_id as string | undefined
          const content = (row.content as string | null) ?? null
          const messageType = (row.message_type as string | undefined) ?? 'text'
          const createdAt = row.created_at as string | undefined

          if (!conversationId || !senderId || !createdAt) return

          // Build the same preview string the server denormalizes — keeps
          // optimistic + server in sync until the next list refetch.
          // No `t` in a hook body — the i18n instance is the same one the
          // provider uses, so the preview follows the UI language instead of
          // leaking Spanish into an English list.
          const preview =
            messageType === 'image'
              ? i18n.t('messages.preview_image')
              : messageType === 'location'
                ? i18n.t('messages.preview_location')
                : (content?.slice(0, 100) ?? '')

          const isOwn = senderId === userId

          let cacheMissed = false
          queryClient.setQueryData<ConversationListItem[]>(conversationKeys.list(userId), (old) => {
            if (!old) {
              cacheMissed = true
              return old
            }
            const idx = old.findIndex((c) => c.id === conversationId)
            if (idx === -1) {
              // First message in a conversation we haven't seen yet — let
              // the fallback invalidate below load it from the server.
              cacheMissed = true
              return old
            }

            const prev = old[idx]!
            const next: ConversationListItem = {
              ...prev,
              lastMessageAt: createdAt,
              lastMessagePreview: preview,
              // Own messages don't bump unread (you can't have unread on
              // a message you sent). Realtime echoes our own sends too.
              unreadCount: isOwn ? prev.unreadCount : prev.unreadCount + 1,
            }

            // Reorder: most-recent first. Drop the old position and
            // splice the bumped item to the head.
            const without = [...old.slice(0, idx), ...old.slice(idx + 1)]
            return [next, ...without]
          })

          // Fall back to a refetch only if the targeted update couldn't
          // find the conversation (new one not yet in the cache).
          if (cacheMissed) {
            queryClient.invalidateQueries({ queryKey: conversationKeys.list(userId) })
          }

          // Tab badge — cheap query, refetch is fine.
          if (!isOwn) {
            queryClient.invalidateQueries({ queryKey: conversationKeys.unreadTotal(userId) })
          }
        },
      )
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return
        if (hasSubscribedOnce) {
          void queryClient.invalidateQueries({ queryKey: conversationKeys.list(userId) })
        }
        hasSubscribedOnce = true
      })

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [userId, queryClient])

  return {
    conversations: query.data ?? [],
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    error: query.error,
    refetch: query.refetch,
  }
}

// ─── Unread Total (for tab badge) ───────────────────────────────────────────

export function useUnreadTotal() {
  const { user } = useAuth()
  const userId = user?.id ?? ''

  const query = useQuery({
    queryKey: conversationKeys.unreadTotal(userId),
    queryFn: () => messagingService.getUnreadTotal(userId),
    enabled: !!userId,
    staleTime: 30 * 1000,
  })

  return query.data ?? 0
}
